import { ICellRendererParams } from '@ag-grid-community/all-modules';
import { createStyles, IconButton, lighten, makeStyles, Theme } from '@material-ui/core';
import { DeleteOutline, InfoOutlined, Stars } from '@material-ui/icons';
import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';

import TnAgGrid from '../../../_components/tn-ag-grid/TnAgGrid';
import TnButton from '../../../_components/tn-button/TnButton';
import TnCheckbox from '../../../_components/tn-checkbox/TnCheckbox';
import TnTextToggle from '../../../_components/tn-text-toggle/TnTextToggle';
import TnTypography from '../../../_components/tn-typography/TnTypography';
import DeleteIcon from '../../../static/_assets/active-quick-actions/delete.svg';

export interface CandidateSkill {
  name: string;
  proficiency_rating: number;
  standout_skill: boolean;
  verified: boolean;
  delete_icon: string;
}

interface Skill {
  name: string;
}

interface SkillTableChangeHandlers {
  onAddSkill: (skill: Skill) => void;
  onChangeSkillProficiency: (skillName: string, proficiency: number) => void;
  onChangeSkillStandout: (skillName: string, standout: boolean) => void;
  onChangeSkillVerified: (skillName: string, verified: boolean) => void;
  onDeleteSkills: (skillNames: string[]) => void;
}

interface Props {
  skills: CandidateSkill[];
  changeHandlers: SkillTableChangeHandlers;
  onClickLearnMore?: () => void;
}

const StandoutCountContext = React.createContext<number>(0);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectedStar: {
      color: '#FFCE62',
      cursor: 'pointer',
    },
    unselectedStar: {
      color: '#828c95',
      cursor: 'pointer',
    },
    checkboxContainer: {
      paddingLeft: 8,
    },
    deleteIconContainer: {
      cursor: 'pointer',
      border: `1px solid ${lighten('#B00020', 0.5)}`,
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    deleteIcon: {
      fill: '#B00020',
    },
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    helperContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: 'calc(100% - 16px)',
      border: `1px solid ${lighten('#000000', 0.8)}`,
      borderRadius: 4,
      padding: 8,
      marginBottom: 16,
    },
    tooltip: {
      color: theme.palette.primary.main,
    },
    textAndIcon: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    standoutSkillsCountContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: 'calc(100% - 32px)',
      border: `1px solid ${lighten('#000000', 0.8)}`,
      borderRadius: 4,
      padding: 16,
      marginBottom: 16,
    },
  }),
);

interface ProficiencyCellRendererProps {
  data: CandidateSkill;
  onChangeSkillProficiency: (skillName: string, proficiency: number) => void;
}

const ProficiencyCellRenderer = React.memo(
  ({ data, onChangeSkillProficiency }: Readonly<ProficiencyCellRendererProps>) => {
    const classes = useStyles();

    const handleClick = React.useCallback(
      (index: number) => {
        onChangeSkillProficiency(data.name, index + 1);
      },
      [onChangeSkillProficiency, data.name],
    );

    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <Stars
            key={index}
            className={classNames({
              [classes.selectedStar]: index < data.proficiency_rating,
              [classes.unselectedStar]: index >= data.proficiency_rating,
            })}
            onClick={() => handleClick(index)}
          />
        ))}
      </>
    );
  },
);

const StandoutSkillCellRenderer = React.memo(
  ({
    data,
    onChangeSkillStandout,
  }: {
    data: CandidateSkill;
    onChangeSkillStandout: (skillName: string, standout: boolean) => void;
  }) => {
    const standoutSkillCount = useContext(StandoutCountContext);

    const handleToggle = React.useCallback(() => {
      if (!data.standout_skill && standoutSkillCount >= 10) {
        return;
      }
      onChangeSkillStandout(data.name, !data.standout_skill);
    }, [onChangeSkillStandout, data.name, data.standout_skill, standoutSkillCount]);

    return (
      <TnTextToggle
        onOption="Standout"
        offOption="Additional"
        value={data.standout_skill ? 'Standout' : 'Additional'}
        onChange={handleToggle}
        disableToggleOn={!data.standout_skill && standoutSkillCount >= 10}
      />
    );
  },
);

export default function SkillsTable({ skills, changeHandlers, onClickLearnMore }: Readonly<Props>) {
  const classes = useStyles();

  const [selectedRows, setSelectedRows] = React.useState<(CandidateSkill & { id: string })[]>([]);

  const columnHeaders = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Skill Name',
        checkboxSelection: true,
        cellRenderer: 'agGroupCellRenderer',
        width: 300,
      },
      {
        field: 'proficiency_rating',
        headerName: 'Click to Rate Skill',
        cellRenderer: 'agGroupCellRenderer',
        width: 250,
        cellRendererParams: {
          innerRendererFramework: (params: ICellRendererParams) => (
            <ProficiencyCellRenderer
              data={params.data}
              onChangeSkillProficiency={changeHandlers.onChangeSkillProficiency}
            />
          ),
        },
      },
      {
        headerName: 'Select a Skill Category',
        field: 'standout_skill',
        cellRenderer: 'agGroupCellRenderer',
        width: 250,
        cellRendererParams: {
          innerRendererFramework: (params: ICellRendererParams) => {
            return (
              <StandoutSkillCellRenderer
                data={params.data}
                onChangeSkillStandout={changeHandlers.onChangeSkillStandout}
              />
            );
          },
        },
      },
      {
        headerName: 'Verification',
        field: 'verified',
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRendererFramework: (params: ICellRendererParams) => {
            return (
              <div className={classes.checkboxContainer}>
                <TnCheckbox
                  onChange={(checked) => {
                    changeHandlers.onChangeSkillVerified(params.data.name, checked);
                  }}
                  label="Verified"
                  value={params.value}
                />
              </div>
            );
          },
        },
        width: 150,
      },
      {
        headerName: '',
        field: 'delete_icon',
        width: 70,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRendererFramework: (params: ICellRendererParams) => {
            return (
              <div
                aria-hidden="true"
                onClick={() => changeHandlers.onDeleteSkills([params.data.name])}
                className={classes.deleteIconContainer}
              >
                <DeleteOutline className={classes.deleteIcon} />
              </div>
            );
          },
        },
      },
    ];
  }, []);

  const iconList = useMemo(() => {
    return [
      {
        icon: DeleteIcon,
        disabledIcon: DeleteIcon,
        key: 'delete_contractor_project',
        name: 'Delete',
        isDisabled: false,
        onClick: () => {
          changeHandlers.onDeleteSkills(selectedRows.map((row) => row.id));
        },
      },
    ];
  }, [selectedRows]);

  const standoutSkillCount = useMemo(() => skills.filter((skill) => skill.standout_skill).length, [skills]);

  const rowData = useMemo(() => {
    return skills.map((skill) => ({
      id: skill.name,
      ...skill,
    }));
  }, [skills]);

  return (
    <>
      <div className={classes.labelContainer}>
        <TnTypography bold variant="body">
          Candidate Skills & Rating
        </TnTypography>
      </div>
      <div className={classes.helperContainer}>
        <div className={classes.textAndIcon}>
          <IconButton>
            <InfoOutlined className={classes.tooltip} />
          </IconButton>
          <TnTypography variant="caption2">
            Need help with the skill ratings? Find out more about how to pick them.
          </TnTypography>
        </div>
        <TnButton variant="outlined" color="secondary" value="Learn More" onClick={() => onClickLearnMore?.()} />
      </div>
      <div className={classes.labelContainer}>
        <TnTypography variant="caption">Enter a maximum of 10 top standout skills for this candidate.</TnTypography>
      </div>
      <div className={classes.standoutSkillsCountContainer}>
        <TnTypography bold variant="caption2">
          Selected {skills.filter((skill) => skill.standout_skill).length}/10 Standout Skills
        </TnTypography>
      </div>
      <StandoutCountContext.Provider value={standoutSkillCount}>
        <TnAgGrid
          columnHeaders={columnHeaders}
          rowData={rowData}
          iconList={iconList}
          isCheckboxRequired={false}
          pagination={true}
          displayQuickActions={true}
          paginationPageSize={10}
          length={500}
          width={'100%'}
          rowsPerPage={10}
          rowsPerPageOptions={[10, 15, 25]}
          immutableData
          onChange={(rows: (CandidateSkill & { id: string })[]) => setSelectedRows(rows)}
        />
      </StandoutCountContext.Provider>
    </>
  );
}
